import { useTranslation } from 'react-i18next';
import {
    HStack,
    VStack,
    Text,
    Button,
    Select,
    Box,
    useToast,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { useAppStore } from '../../state/store';
import { useBIMStore } from '../../state/useBIMStore';
import { ManageDialogFrame } from '../manage/ManageDialogFrame';
import {
    usePrecastConfigStore,
    PrecastConfiguration,
    PRECAST_CAM_FORMATS,
} from '../../state/usePrecastConfigStore';
import { downloadTextFile } from '../../services/fileSystem/fileAccess';
import { buildCamPayload } from '../../utils/precastFabrication';
import { collectPrecastPanels, collectPrecastAttachments } from './PrecastShopDrawingsDialog';

/**
 * Precast CAM Export dialog (BIM: Precast > Fabrication > CAM Export).
 * Converts the precast model (panels + reinforcement + embeds) into factory
 * machine data: PXML / Unitechnik XML for bed and robot stations, or CSV
 * flavors for bar-bending and cut-list stations. The active Configuration
 * format is pre-selected; the export runs locally through the file gateway.
 */
export function PrecastCamExportDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'precastCamExport');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const project = useBIMStore((state) => state.project);
    const config = usePrecastConfigStore((state) => state.config);
    const update = usePrecastConfigStore((state) => state.updateConfig);
    const toast = useToast();

    const panels = useMemo(() => collectPrecastPanels(project.elements), [project]);
    const attachments = useMemo(() =>collectPrecastAttachments(project.elements), [project]); const [scope, setScope] = useState<'all' | 'reinforced'>('all');

    const scopePanels = scope === 'all'
        ? panels
        : panels.filter(p => attachments.reinf.some(r => r.geometryData.hostId === p.id));

    const handleExport = () => {
        if (scopePanels.length === 0) {
            toast({ title: 'CAM Export: no precast panels in scope', status: 'warning', duration: 2500 });
            return;
        }
        const payload = buildCamPayload(
            scopePanels,
            attachments.reinf,
            attachments.mounting,
            config,
        );
        downloadTextFile(payload.fileName, payload.content, payload.mime);
        toast({
            title: `Exported ${scopePanels.length} panel(s) → ${payload.fileName}`,
            description: `${config.camFormat} · ${config.factoryName}`,
            status: 'success',
            duration: 3500,
        });
    };

    const summary = useMemo(() => {
        const embeds = attachments.mounting.filter(m => scopePanels.some(p => p.id === m.geometryData.hostId)).length;
        const meshes = attachments.reinf.filter(r => scopePanels.some(p => p.id === r.geometryData.hostId)).length;
        return { embeds, meshes };
    }, [scopePanels, attachments]);

    return (
        <ManageDialogFrame
            title={t('misc.precastCAMExport')}
            isOpen={isOpen}
            onClose={onClose}
            size="md"
            footer={
                <HStack justify="space-between" w="full">
                    <Text fontSize="xs" color="text.secondary">
                        {scopePanels.length} panel(s) · {summary.meshes} reinforcement mesh(es) · {summary.embeds} embedded part(s)
                    </Text>
                    <HStack>
                        <Button variant="ghost" size="sm" onClick={onClose} color="var(--color-text-secondary)">{t('misc.cancel')}</Button>
                        <Button size="sm" colorScheme="blue" onClick={handleExport} isDisabled={scopePanels.length === 0}>{t('misc.export')}</Button>
                    </HStack>
                </HStack>
            }
        >
            <VStack align="stretch" spacing={4} py={2}>
                <HStack justify="space-between">
                    <Text fontSize="xs" fontWeight="bold" w="140px" flexShrink={0}>{t('misc.machineFormat')}</Text>
                    <Select
                        size="sm"
                        value={config.camFormat}
                        onChange={(e) => update({ camFormat: e.target.value as PrecastConfiguration['camFormat'] })}
                    >
                        {PRECAST_CAM_FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
                    </Select>
                </HStack>
                <HStack justify="space-between">
                    <Text fontSize="xs" fontWeight="bold" w="140px" flexShrink={0}>{t('misc.factoryPreset')}</Text>
                    <Select
                        size="sm"
                        value={config.factoryName}
                        onChange={(e) => update({ factoryName: e.target.value })}
                    >
                        <option value="Yap Precast Plant">{t('misc.yapPrecastPlant')}</option>
                        <option value="Elematic Line">{t('misc.elematicLine')}</option>
                        <option value="Spechorn Formax">{t('misc.spechornFormax')}</option>
                        <option value="Vollert VETEX">{t('misc.vollertVETEX')}</option>
                    </Select>
                </HStack>
                <HStack justify="space-between">
                    <Text fontSize="xs" fontWeight="bold" w="140px" flexShrink={0}>{t('misc.selectionScope')}</Text>
                    <Select
                        size="sm"
                        value={scope}
                        onChange={(e) => setScope(e.target.value as 'all' | 'reinforced')}
                    >
                        <option value="all">All precast panels ({panels.length})</option>
                        <option value="reinforced">{t('misc.reinforcedPanelsOnly')}</option>
                    </Select>
                </HStack>
                <Box borderWidth="1px" borderColor="border.base" borderRadius="sm" p={3}>
                    <Text fontSize="xs" color="text.secondary" lineHeight="1.6">{t('misc.exportedDataPanelOutlinesWithJointGapsReinforcementBarListsAndLe')}</Text>
                </Box>
                {panels.length === 0 && (
                    <Text fontSize="xs" color="status.warning">{t('misc.theModelHasNoPrecastPanelsYetSplitAWallOrFloorWithPrecastSplit')}</Text>
                )}
            </VStack>
        </ManageDialogFrame>
    );
}
